import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';

import { Observable, Subscription, of } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { BaseMediaApiModel, MediaMovieApiModel } from '@era/inroom-sdk';
import { isNotNullOrUndefined, isNotUndefined } from '@rocksoft/shared';

import { MediaStateChangedEvent } from '../../../shared/events/media-state-changed.event';

import { AsyncPipe } from '@angular/common';
import { MediaStateComponent } from './media-state/media-state.component';
import { selectCurrentMediaState } from './media.selectors';
import { MovieComponent } from './movie/movie.component';
import { getMovie } from './movie/movies.selector';
import { PlayerComponent } from './player/player.component';
import { RadioChannelComponent } from './radio-channel/radio-channel.component';
import { getRadioChannel } from './radio-channel/radio-channels.selector';
import { TvChannelComponent } from './tv-channel/tv-channel.component';
import { getTvChannel } from './tv-channel/tv-channels.selector';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'era-media',
  templateUrl: './media.container.html',
  imports: [MediaStateComponent, PlayerComponent, MovieComponent, TvChannelComponent, RadioChannelComponent, AsyncPipe],
})
export class MediaContainerComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);

  private readonly playerComponent = viewChild.required(PlayerComponent);
  private readonly subscriptions: Subscription[] = [];

  public readonly mediaState$: Observable<MediaStateChangedEvent>;
  public readonly movie$: Observable<MediaMovieApiModel>;
  public readonly tvChannel$: Observable<BaseMediaApiModel>;
  public readonly radioChannel$: Observable<BaseMediaApiModel>;

  constructor() {
    this.mediaState$ = this.store.select(selectCurrentMediaState).pipe(filter(isNotUndefined));

    this.movie$ = this.mediaState$.pipe(
      switchMap((state) => {
        if (state?.CurrentlyPlayingTypeCode !== 'Movie') {
          return of(null);
        }
        return this.store.select(getMovie(state.CurrentlyPlayingCode ?? ''));
      }),
      filter(isNotNullOrUndefined),
      distinctUntilChanged()
    );

    this.tvChannel$ = this.mediaState$.pipe(
      switchMap((state) => {
        if (state?.CurrentlyPlayingTypeCode !== 'Television') {
          return of(null);
        }
        return this.store.select(getTvChannel(state.CurrentlyPlayingCode ?? ''));
      }),
      filter(isNotNullOrUndefined),
      distinctUntilChanged()
    );

    this.radioChannel$ = this.mediaState$.pipe(
      switchMap((state) => {
        if (state?.CurrentlyPlayingTypeCode !== 'Radio') {
          return of(null);
        }
        return this.store.select(getRadioChannel(state.CurrentlyPlayingCode ?? ''));
      }),
      filter(isNotNullOrUndefined),
      distinctUntilChanged()
    );
  }

  ngOnInit() {
    this.subscriptions.push(
      this.movie$.subscribe((movie) => {
        if (movie) {
          this.playerComponent().playSearch(`${movie.Name ?? ''} ${movie.Year?.toString() ?? ''} trailer`);
        }
      }),
      this.tvChannel$.subscribe((tvChannel) => {
        if (tvChannel) {
          this.playerComponent().playSearch(`${tvChannel.Name ?? ''} television`);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
